import { useState, useEffect, useRef, useCallback } from 'react';
import { HyperFormula } from 'hyperformula';
import { devLog } from '../utils/devLogger';

interface InputInfo {
  element: HTMLInputElement;
  memberName: string;
  contextRef: string;
  cellAddress: any;
  isTotal: boolean;
}

interface RowData {
  inputs: InputInfo[];
  memberInputs: InputInfo[];
  totalInput: InputInfo | null;
}

interface UseHyperFormulaNoteReturn {
  engine: HyperFormula | null;
  isReady: boolean;
  handleFieldChange: (fieldName: string, contextRef: string, value: string) => void;
}

/**
 * Simplified HyperFormula hook for Note tables (Share Capital, Related Party)
 * Only handles horizontal sums: Total = sum of all member columns
 * No vertical calculations, no hierarchy
 */
export const useHyperFormulaNote = (
  formStructure: any = null
): UseHyperFormulaNoteReturn => {
  const [engine, setEngine] = useState<HyperFormula | null>(null);
  const [isReady, setIsReady] = useState(false);

  const sheetRef = useRef<number>(0);
  const inputsByRowRef = useRef<Map<number, RowData>>(new Map());
  const rowIndicesRef = useRef<Map<HTMLTableRowElement, number>>(new Map());
  const updatingCellRef = useRef<boolean>(false);

  // Format number
  const formatNumber = useCallback((value: any): string => {
    if (typeof value !== 'number') {
      value = 0;
    }

    if (typeof window !== 'undefined' && (window as any).numeral) {
      return (window as any).numeral(value).format('0,0.00');
    }

    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }, []);

  // Map inputs by row
  const mapInputsByRow = useCallback((hfEngine: HyperFormula) => {
    let cellCount = 0;

    const rows = document.querySelectorAll('.sum-calculation-table tr');
    rows.forEach((row, index) => {
      rowIndicesRef.current.set(row as HTMLTableRowElement, index);
    });

    rows.forEach(row => {
      const rowIndex = rowIndicesRef.current.get(row as HTMLTableRowElement);
      if (rowIndex === undefined) return;

      const rowData: RowData = {
        inputs: [],
        memberInputs: [],
        totalInput: null
      };

      row.querySelectorAll('input[data-note-member], input[data-note-total]').forEach(inputEl => {
        const input = inputEl as HTMLInputElement;
        const memberName = input.dataset.noteMember || '';
        const contextRef = input.dataset.contextRef;
        const isTotal = input.dataset.noteTotal === 'true';

        if (!contextRef) return;

        const cellAddress = {
          sheet: sheetRef.current,
          col: 0,
          row: cellCount++
        };

        const inputInfo: InputInfo = {
          element: input,
          memberName,
          contextRef,
          cellAddress,
          isTotal
        };

        rowData.inputs.push(inputInfo);

        if (isTotal) {
          rowData.totalInput = inputInfo;
          input.readOnly = true;
        } else {
          rowData.memberInputs.push(inputInfo);
        }

        let value = 0;
        if (input.value && input.value.trim() !== '') {
          if ((window as any).numeral) {
            value = (window as any).numeral(input.value).value() || 0;
          } else {
            value = parseFloat(input.value.replace(/[^\d.-]/g, '')) || 0;
          }
        }

        hfEngine.setCellContents(cellAddress, value);
      });

      if (rowData.inputs.length > 0) {
        inputsByRowRef.current.set(rowIndex, rowData);
      }
    });
  }, []);

  // Setup formulas: Total = sum of all member columns
  const setupFormulas = useCallback((hfEngine: HyperFormula) => {
    inputsByRowRef.current.forEach((rowData, rowIndex) => {
      if (!rowData.totalInput || rowData.memberInputs.length === 0) return;

      const formulaParts: string[] = [];

      rowData.memberInputs.forEach(memberInfo => {
        const cellRef = hfEngine.simpleCellAddressToString(memberInfo.cellAddress, sheetRef.current);
        if (cellRef) {
          formulaParts.push(cellRef);
        }
      });

      if (formulaParts.length > 0) {
        const formula = '=' + formulaParts.join('+');
        hfEngine.setCellContents(rowData.totalInput.cellAddress, formula);
      }
    });
  }, []);

  // Refresh total calculations in a row
  const refreshRowTotals = useCallback((rowIndex: number) => {
    if (!engine) return;

    const rowData = inputsByRowRef.current.get(rowIndex);
    if (!rowData || !rowData.totalInput) return;

    const value = engine.getCellValue(rowData.totalInput.cellAddress);
    const formattedValue = formatNumber(value);

    if (rowData.totalInput.element.value !== formattedValue) {
      rowData.totalInput.element.value = formattedValue;

      // Dispatch event to notify React
      rowData.totalInput.element.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
        detail: {
          fieldName: rowData.totalInput.element.name,
          contextRef: rowData.totalInput.element.dataset.contextRef,
          value: formattedValue
        }
      }));
    }
  }, [engine, formatNumber]);

  // Handle input change
  const handleInputChange = useCallback((input: HTMLInputElement) => {
    if (updatingCellRef.current || !engine) return;
    updatingCellRef.current = true;

    try {
      const contextRef = input.dataset.contextRef;
      if (!contextRef) return;

      const row = input.closest('tr');
      if (!row) return;

      const rowIndex = rowIndicesRef.current.get(row as HTMLTableRowElement);
      if (rowIndex === undefined) return;

      const rowData = inputsByRowRef.current.get(rowIndex);
      if (!rowData) return;

      const inputInfo = rowData.inputs.find(info =>
        info.element === input || info.contextRef === contextRef
      );

      if (!inputInfo) return;

      let value = 0;
      if (input.value && input.value.trim() !== '') {
        if ((window as any).numeral) {
          value = (window as any).numeral(input.value).value() || 0;
        } else {
          value = parseFloat(input.value.replace(/[^\d.-]/g, '')) || 0;
        }
      }

      engine.setCellContents(inputInfo.cellAddress, value);
      refreshRowTotals(rowIndex);
    } catch (error) {
      console.error("Error handling input change:", error);
    } finally {
      updatingCellRef.current = false;
    }
  }, [engine, refreshRowTotals]);

  // Public API: handleFieldChange
  const handleFieldChangeAPI = useCallback((fieldName: string, contextRef: string, value: string) => {
    if (!engine) return;

    const input = document.querySelector(
      `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
    ) as HTMLInputElement;

    if (input) {
      input.value = value;
      handleInputChange(input);
    }
  }, [engine, handleInputChange]);

  // Setup event listeners
  useEffect(() => {
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.matches('.sum-calculation-table input[data-note-member]:not([readonly])')) {
        handleInputChange(target);
      }
    };

    const handleChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.matches('.sum-calculation-table input[data-note-member]:not([readonly])')) {
        handleInputChange(target);
      }
    };

    document.addEventListener('input', handleInput);
    document.addEventListener('change', handleChange);

    return () => {
      document.removeEventListener('input', handleInput);
      document.removeEventListener('change', handleChange);
    };
  }, [handleInputChange]);

  // Initialize HyperFormula engine
  useEffect(() => {
    if (engine) return;

    try {
      const hfEngine = HyperFormula.buildEmpty({
        licenseKey: 'gpl-v3',
        precisionRounding: 10,
        useColumnIndex: false
      });

      const sheetName = hfEngine.addSheet('note');
      const sheetId = hfEngine.getSheetId(sheetName);
      sheetRef.current = sheetId || 0;

      setEngine(hfEngine);

      // Delay mapping until DOM is ready
      setTimeout(() => {
        const noteTable = document.querySelector('.sum-calculation-table');
        if (!noteTable) {
          console.warn('Note table not found, skipping initialization');
          setIsReady(false);
          return;
        }

        // Map inputs by row
        mapInputsByRow(hfEngine);

        // Setup formulas
        setupFormulas(hfEngine);

        // Initial calculation - populate all total fields with 0.00
        inputsByRowRef.current.forEach((rowData, rowIndex) => {
          if (rowData.totalInput) {
            const value = hfEngine.getCellValue(rowData.totalInput.cellAddress);
            const formattedValue = formatNumber(value);
            rowData.totalInput.element.value = formattedValue;

            // Dispatch event to notify React
            rowData.totalInput.element.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
              detail: {
                fieldName: rowData.totalInput.element.name,
                contextRef: rowData.totalInput.element.dataset.contextRef,
                value: formattedValue
              }
            }));
          }
        });

        setIsReady(true);
      }, 500);
    } catch (error) {
      console.error("Error initializing Note calculator:", error);
      setIsReady(false);
    }
  }, []);

  // Listen for formDataLoaded event to re-read from DOM (like mbrs 1.0)
  useEffect(() => {
    if (!engine) return;

    const handleFormDataLoaded = () => {
      devLog('HyperFormula Note: formDataLoaded event - fast sync (no remap)...');

      setTimeout(() => {
        // Update ONLY member cells (keep Total formula intact)
        inputsByRowRef.current.forEach((rowData, rowIndex) => {
          rowData.memberInputs.forEach((info) => {
            let value = 0;
            const raw = info.element.value;
            if (raw && raw.trim() !== '') {
              if ((window as any).numeral) {
                value = (window as any).numeral(raw).value() || 0;
              } else {
                value = parseFloat(raw.replace(/[^\d.-]/g, '')) || 0;
              }
            }
            engine.setCellContents(info.cellAddress, value);
          });

          // Refresh this row's total
          refreshRowTotals(rowIndex);
        });

        devLog('HyperFormula Note: fast sync complete');

        // Signal completion for coordinator
        document.dispatchEvent(new CustomEvent('noteCalculationsComplete'));
      }, 0);
    };
    
    document.addEventListener('formDataLoaded', handleFormDataLoaded);
    
    return () => {
      document.removeEventListener('formDataLoaded', handleFormDataLoaded);
    };
  }, [engine, mapInputsByRow, setupFormulas, formatNumber]);

  return {
    engine,
    isReady,
    handleFieldChange: handleFieldChangeAPI
  };
};

